import { Button, Container, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import ApiKeyModal from "../components/apiKeys/ApiKeyModal";
import ApiKeyTable from "../components/apiKeys/ApiKeyTable";
import EmptyState from "../components/common/EmptyState";
import LoadingSpinner from "../components/common/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import { apiKeysAPI, type ApiKey } from "../services/api";

const showRequestError = (error: unknown, fallback: string) => {
	const message = (error as { response?: { data?: { message?: string } } })
		?.response?.data?.message;
	notifications.show({ title: "Error", message: message || fallback, color: "red" });
};

function ApiKeysPage() {
	const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
	const [loading, setLoading] = useState(true);
	const [modalOpened, setModalOpened] = useState(false);
	const [creating, setCreating] = useState(false);
	const [createdKey, setCreatedKey] = useState<string | null>(null);

	const loadApiKeys = useCallback(async () => {
		try {
			const response = await apiKeysAPI.getAll();
			setApiKeys(response.data.data?.apiKeys ?? []);
		} catch (error) {
			showRequestError(error, "Failed to load API keys");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadApiKeys();
	}, [loadApiKeys]);

	const handleCreate = async (name: string) => {
		setCreating(true);
		try {
			const response = await apiKeysAPI.create(name);
			setCreatedKey(response.data.data?.key ?? null);
			loadApiKeys();
		} catch (error) {
			showRequestError(error, "Failed to create API key");
		} finally {
			setCreating(false);
		}
	};

	const handleCloseModal = () => {
		setModalOpened(false);
		setCreatedKey(null);
	};

	const setActive = async (apiKey: ApiKey, isActive: boolean) => {
		try {
			await apiKeysAPI.setActive(apiKey._id, isActive);
			notifications.show({
				title: "Success",
				message: `API key "${apiKey.name}" ${isActive ? "activated" : "deactivated"}`,
				color: "green",
			});
			loadApiKeys();
		} catch (error) {
			showRequestError(error, "Failed to update API key");
		}
	};

	// Deactivating cuts the caller off on its next request, so it is confirmed;
	// activating is not
	const handleSetActive = (apiKey: ApiKey, isActive: boolean) => {
		if (isActive) {
			setActive(apiKey, true);
			return;
		}
		modals.openConfirmModal({
			title: "Deactivate API Key",
			children: (
				<Text size="sm">
					Every request using "{apiKey.name}" (••••{apiKey.hint}) will be refused
					from now on. Anything still calling with it - spaceOS included, if this
					is its key - stops working until the key is activated again.
				</Text>
			),
			labels: { confirm: "Deactivate", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: () => setActive(apiKey, false),
		});
	};

	return (
		<Container size="xl">
			<PageHeader
				title="API Keys"
				description="Keys that let other systems, such as spaceOS, call the license service endpoints"
				actions={
					<Button
						leftSection={<IconPlus size={16} />}
						onClick={() => setModalOpened(true)}>
						Create API Key
					</Button>
				}
			/>

			{loading ? (
				<LoadingSpinner />
			) : apiKeys.length === 0 ? (
				<EmptyState
					title="No API keys"
					message="Create a key for each system that calls the license service endpoints."
				/>
			) : (
				<ApiKeyTable
					apiKeys={apiKeys}
					onSetActive={handleSetActive}
				/>
			)}

			<ApiKeyModal
				opened={modalOpened}
				loading={creating}
				createdKey={createdKey}
				onClose={handleCloseModal}
				onSubmit={handleCreate}
			/>
		</Container>
	);
}

export default ApiKeysPage;
